<script lang="ts">
export default {
  name: 'ColorPickerMenu',
};
</script>

<script setup lang="ts">
import { ref, watch, computed, nextTick, onBeforeUnmount } from 'vue';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { Check, Edit2, Plus, X } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';
import { useAnchoredMenuPosition } from '@/composables/useAnchoredMenuPosition';

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

interface ColorSwatchItem {
  type: 'default' | 'custom';
  color: string;
}

const props = defineProps<{
  colors: string[];
  color?: string;
}>();

const STORAGE_KEY = 'customColorMemory';

const menuOpen = ref(false);
const showColorPicker = ref(false);
const editMode = ref(false);
const colorPickerSelection = ref('#FF0000');
const customSwatches = ref<ColorSwatchItem[]>([]);
const triggerRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const { menuStyle, updateMenuPosition } = useAnchoredMenuPosition(triggerRef, menuRef, { start: true });
let clickOutsideCleanup: (() => void) | null = null;

const allSwatches = computed(() => {
  const defaults = props.colors.map((c) => ({ type: 'default' as const, color: c }));
  return [...defaults, ...customSwatches.value];
});

function loadCustomColors() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.color) {
      customSwatches.value = parsed.map((c: { color: string }) => ({ type: 'custom' as const, color: c.color }));
    } else {
      customSwatches.value = [];
      localStorage.setItem(STORAGE_KEY, '[]');
    }
  } catch {
    customSwatches.value = [];
    localStorage.setItem(STORAGE_KEY, '[]');
  }
}

function saveCustomColors() {
  const toSave = customSwatches.value.map((s) => ({ type: s.type, color: s.color }));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
}

function openMenu() {
  menuOpen.value = true;
  loadCustomColors();
}

function closeMenu() {
  menuOpen.value = false;
  showColorPicker.value = false;
  editMode.value = false;
}

function handleClickOutside(e: MouseEvent) {
  const target = e.target as Node;
  if (triggerRef.value?.contains(target) || menuRef.value?.contains(target)) return;
  closeMenu();
}

function selectColor(color: string) {
  emit('update:modelValue', color);
  closeMenu();
}

function handleAddCustomColor() {
  colorPickerSelection.value = props.color || '#FF0000';
  showColorPicker.value = true;
}

function handleCancelColorPicker() {
  showColorPicker.value = false;
}

function handleSaveCustomColor() {
  const color = colorPickerSelection.value.toLowerCase();
  if (allSwatches.value.some((s) => s.color.toLowerCase() === color)) {
    showColorPicker.value = false;
    return;
  }
  customSwatches.value = [...customSwatches.value, { type: 'custom', color: color }];
  saveCustomColors();
  emit('update:modelValue', color);
  showColorPicker.value = false;
  closeMenu();
}

function removeCustomColor(swatch: ColorSwatchItem, e: Event) {
  e.stopPropagation();
  customSwatches.value = customSwatches.value.filter((s) => s !== swatch);
  saveCustomColors();
  if (props.color === swatch.color) {
    const fallback = props.colors[0] ?? customSwatches.value[0]?.color ?? '#000000';
    emit('update:modelValue', fallback);
  }
}

function toggleEditMode() {
  editMode.value = !editMode.value;
}

watch(menuOpen, (open) => {
  if (!open) {
    menuStyle.value = null;
    clickOutsideCleanup?.();
    clickOutsideCleanup = null;
    return;
  }
  nextTick(() => {
    updateMenuPosition();
    clickOutsideCleanup = () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
    document.addEventListener('click', handleClickOutside, true);
  });
});

watch(showColorPicker, (open) => {
  Commit(StoreMutationType.SET_IS_EDITING, open);
});

onBeforeUnmount(() => {
  clickOutsideCleanup?.();
});
</script>

<template>
  <div class="relative inline-block">
    <!-- Trigger: small color square -->
    <button
      ref="triggerRef"
      type="button"
      class="h-[26px] w-[26px] min-w-[26px] rounded border border-solid border-gray-400 p-0 transition-colors outline-none hover:border-gray-500 focus:ring-2 focus:ring-primary/50"
      :style="{ backgroundColor: props.color || '#FF0000' }"
      aria-haspopup="true"
      :aria-expanded="menuOpen"
      @click="menuOpen ? closeMenu() : openMenu()" />

    <!-- Menu (teleported) -->
    <Teleport to="body">
      <div
        v-if="menuOpen"
        ref="menuRef"
        class="fixed z-9999 flex min-w-[200px] flex-col gap-3 overflow-hidden rounded-lg border border-solid border-tower-border bg-tower-alt p-3 shadow-lg"
        :style="menuStyle || undefined"
        @click.stop>
        <!-- Swatch grid (hidden when color picker is shown) -->
        <div
          v-show="!showColorPicker"
          class="flex flex-col gap-2">
          <div class="flex items-center justify-between gap-2">
            <span class="px-2 text-xs">Custom colors</span>
            <button
              type="button"
              class="flex h-6 w-6 items-center justify-center rounded outline outline-tower-border hover:bg-gray-600/10"
              :class="editMode ? 'bg-gray-600/20' : ''"
              @click="toggleEditMode">
              <Check
                v-if="editMode"
                :size="14"
                class="text-green-600" />
              <Edit2
                v-else
                :size="14" />
            </button>
          </div>
          <div class="flex flex-wrap gap-1.5">
            <template
              v-for="(swatch, idx) in allSwatches"
              :key="`${swatch.type}-${swatch.color}-${idx}`">
              <div class="relative">
                <button
                  type="button"
                  class="h-7 w-7 min-w-[28px] rounded border-2 border-solid p-0 transition-colors outline-none"
                  :class="
                    props.color === swatch.color ? 'border-gray-900 ring-1 ring-gray-400' : 'border-transparent hover:border-gray-500'
                  "
                  :style="{ backgroundColor: swatch.color }"
                  @click="selectColor(swatch.color)" />
                <button
                  v-if="editMode && swatch.type === 'custom'"
                  type="button"
                  class="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-gray-600 text-white opacity-90 hover:opacity-100"
                  @click="removeCustomColor(swatch, $event)">
                  <X :size="10" />
                </button>
              </div>
            </template>
            <button
              type="button"
              class="flex h-7 w-7 min-w-[28px] items-center justify-center rounded border-2 border-dashed border-tower-border bg-transparent hover:bg-gray-600/10"
              @click="handleAddCustomColor">
              <Plus :size="14" />
            </button>
          </div>
        </div>

        <!-- Color picker panel -->
        <div
          v-show="showColorPicker"
          class="flex flex-col gap-2">
          <input
            v-model="colorPickerSelection"
            type="color"
            class="h-[100px] w-full cursor-pointer rounded border border-solid border-tower-border p-0" />
          <div class="flex justify-end gap-2">
            <AppButton
              outline
              @click="handleCancelColorPicker"
              >Cancel</AppButton
            >
            <AppButton
              primary
              :disabled="allSwatches.some((s) => s.color.toLowerCase() === colorPickerSelection.toLowerCase())"
              @click="handleSaveCustomColor">
              Save
            </AppButton>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
